import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    legacyApi,
    Customer,
    SaleOrder,
    SaleOrderOverview,
    DeliveryNoteSummary,
    UnbilledDeliveryPreview,
    DocumentAttachment,
    WorkflowEntityDefinition,
    WorkflowEventDefinition,
    WorkflowHistoryEntry
} from '../../../services/legacyApi';
import { api } from '../../../services/api';
import { ArrowLeft, Download, FileText, Link2, Loader2, MessageCircle, Send, Truck, Eye, Trash2, Pencil, Save, X, CreditCard, Plus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { getDeliveryStatusMeta, getInvoiceStatusMeta, getSaleOrderStatusMeta } from '../utils/statuses';
import { TransitionActionBar } from '../components/TransitionActionBar';
import { StatusGuidePanel } from '../components/StatusGuidePanel';
import { StatusHistoryPanel } from '../components/StatusHistoryPanel';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { useAuth } from '../../../context/AuthContext';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { DocumentAttachmentsCard } from '../components/DocumentAttachmentsCard';
import { DocumentPreviewDialog } from '../components/DocumentPreviewDialog';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { openWhatsAppMessage } from '../utils/whatsapp';
import { RelatedOpenSaleOrdersNotice } from '../components/RelatedOpenSaleOrdersNotice';

export const LegacySaleOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { confirm } = useConfirm();
    const { hasPermission } = useAuth();
    const [order, setOrder] = useState<SaleOrder | null>(null);
    const [deliveries, setDeliveries] = useState<DeliveryNoteSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [editedDetails, setEditedDetails] = useState<SaleOrder['details']>([]);
    const [editedRemarks, setEditedRemarks] = useState('');
    const [editedPoRemarks, setEditedPoRemarks] = useState('');
    const [editedVatPercent, setEditedVatPercent] = useState<number>(0);
    const [editedDiscountPercent, setEditedDiscountPercent] = useState<number>(0);
    const [editedOverrideReason, setEditedOverrideReason] = useState('');
    const [editedCustomerId, setEditedCustomerId] = useState<number | ''>('');
    const [editedCustomerPoNo, setEditedCustomerPoNo] = useState('');
    const [editedSoDate, setEditedSoDate] = useState('');
    const [editedPoIssueDate, setEditedPoIssueDate] = useState('');
    const [editedFlowMode, setEditedFlowMode] = useState<'SIMPLE' | 'PRODUCTION'>('PRODUCTION');
    const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
    const [saving, setSaving] = useState(false);
    const [transitionBusy, setTransitionBusy] = useState<string | null>(null);
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);
    const [overview, setOverview] = useState<SaleOrderOverview | null>(null);
    const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [previewDocument, setPreviewDocument] = useState<DocumentAttachment | null>(null);
    const [creatingWorkOrder, setCreatingWorkOrder] = useState(false);
    const [showConsolidatedInvoicePanel, setShowConsolidatedInvoicePanel] = useState(false);
    const [consolidatedCutoffDate, setConsolidatedCutoffDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [consolidatedPreview, setConsolidatedPreview] = useState<UnbilledDeliveryPreview | null>(null);
    const [loadingConsolidatedPreview, setLoadingConsolidatedPreview] = useState(false);
    const [creatingConsolidatedInvoice, setCreatingConsolidatedInvoice] = useState(false);
    const consolidatedInvoicePanelRef = useRef<HTMLDivElement | null>(null);
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });
    const toDateInputValue = (value?: string | null) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 10);
    };

    const deliveryColumns = [
        { header: "DN Number", accessor: (dn: DeliveryNoteSummary) => dn.dn_number, className: 'font-medium' },
        { header: "Date", accessor: (dn: DeliveryNoteSummary) => new Date(dn.dn_date).toLocaleDateString(), className: '' },
        {
            header: "Status",
            accessor: (dn: DeliveryNoteSummary) => {
                const status = getDeliveryStatusMeta(Number(dn.status_id || 1));
                return (
                    <span className={`px-2 py-1 rounded-full text-xs border ${status.badgeClass}`}>
                        {status.label}
                    </span>
                );
            },
        },
        { header: "Items", accessor: (dn: DeliveryNoteSummary) => dn.item_count, className: 'text-center' },
        {
            header: "Actions",
            accessor: (dn: DeliveryNoteSummary) => (
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/legacy/delivery/${dn.dn_id}`)}>
                        <Eye className="h-4 w-4 mr-2" /> View
                    </Button>
                    {Number(dn.status_id || 1) === 1 ? (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/legacy/delivery/${dn.dn_id}?edit=1`)}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                        </Button>
                    ) : (
                        <Button variant="ghost" size="sm" disabled title="Only Draft delivery notes can be edited.">
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={async () => {
                            const confirmed = await confirm(`Delete Delivery Note ${dn.dn_number}?`, {
                                description: 'This will remove the delivery note and its items.',
                                confirmText: 'Delete',
                                variant: 'destructive'
                            });
                            if (!confirmed) return;
                            try {
                                await legacyApi.sales.deleteDelivery(dn.dn_id);
                                setDeliveries(prev => prev.filter(d => d.dn_id !== dn.dn_id));
                            } catch (e: any) {
                                toast.error(e?.message || 'Failed to delete delivery note');
                            }
                        }}
                    >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                </div>
            ),
            className: 'text-right'
        }
    ];

    const loadOrderDocuments = async (soId: number, options?: { silent?: boolean }) => {
        if (!options?.silent) setDocumentsLoading(true);
        try {
            const rows = await legacyApi.sales.listOrderDocuments(soId, 'LPO');
            setDocuments(Array.isArray(rows) ? rows : []);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load LPO documents');
            setDocuments([]);
        } finally {
            if (!options?.silent) setDocumentsLoading(false);
        }
    };

    const refreshOrderContext = async (soId: number) => {
        const [orderData, deliveryData, definition, actions, historyResult, overviewData, documentData] = await Promise.all([
            legacyApi.sales.getOrder(soId),
            legacyApi.sales.getDeliveriesForOrder(soId).catch(() => []),
            legacyApi.workflow.getDefinition('SO').catch(() => null),
            legacyApi.workflow.getNextActions('SO', soId).catch(() => ({ next_actions: [] })),
            legacyApi.workflow.getHistory('SO', soId).catch(() => ({ history: [] })),
            legacyApi.sales.getOrderOverview(soId).catch(() => null),
            legacyApi.sales.listOrderDocuments(soId, 'LPO').catch(() => [] as DocumentAttachment[]),
        ]);
        setOrder(orderData);
        setDeliveries(deliveryData);
        setEditedDetails(orderData.details ? [...orderData.details] : []);
        setEditedRemarks(orderData.so_remarks || '');
        setEditedPoRemarks(orderData.po_remarks || '');
        setEditedVatPercent(Number(orderData.vat_percent || 0));
        setEditedDiscountPercent(Number(orderData.discount_percent || 0));
        setEditedOverrideReason('');
        setEditedCustomerId(typeof orderData.customer_id === 'number' ? Number(orderData.customer_id) : '');
        setEditedCustomerPoNo(orderData.customer_po_no || '');
        setEditedSoDate(toDateInputValue(orderData.so_date));
        setEditedPoIssueDate(toDateInputValue(orderData.po_issue_date));
        setEditedFlowMode(
            String(orderData.flow_mode || orderData.resolved_flow_mode || 'PRODUCTION').trim().toUpperCase() === 'SIMPLE'
                ? 'SIMPLE'
                : 'PRODUCTION'
        );
        setWorkflowDefinition(definition);
        setNextActions((actions as any).next_actions || []);
        setHistory((historyResult as any).history || []);
        setOverview(overviewData);
        setDocuments(Array.isArray(documentData) ? documentData : []);
    };

    useEffect(() => {
        legacyApi.customers.list()
            .then((rows) => setCustomerOptions(Array.isArray(rows) ? rows : []))
            .catch(() => {
                setCustomerOptions([]);
            });
    }, []);

    useEffect(() => {
        if (!id) return;
        const soId = parseInt(id, 10);

        refreshOrderContext(soId)
            .then(() => setEditMode(searchParams.get('edit') === '1'))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, searchParams]);

    // Keep overview in-sync when user returns to this tab/page (e.g. WO changed elsewhere)
    useEffect(() => {
        if (!order) return;
        const onFocus = () => {
            // refresh only the overview portion to update work order status badges
            refreshOrderContext(Number(order.so_id)).catch(() => {
                /* ignore refresh errors */
            });
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [order, refreshOrderContext]);

    const handleDetailFieldChange = (index: number, field: keyof NonNullable<SaleOrder['details']>[number], value: any) => {
        setEditedDetails(prev => prev?.map((item, idx) => idx === index ? { ...item, [field]: value } : item) || []);
    };

    const handleAddLine = () => {
        if (!canEditLineItems || !editMode) return;
        setEditedDetails((prev) => ([
            ...(prev || []),
            {
                so_detail_id: undefined,
                estimate_part_id: undefined,
                item_id: undefined,
                line_source: 'DIRECT_ITEM',
                part_name: '',
                formatted_part_id: '-',
                quantity: 1,
                unit_price: 0,
                amount: 0,
                unit: undefined,
                unit_label: undefined,
                material_name: undefined,
                item_name: undefined,
                material_type_id: undefined,
                material_type: undefined,
                thickness: undefined,
                category: '',
                color_finish: '',
            },
        ]));
    };

    const handleRemoveLine = (index: number) => {
        if (!canEditLineItems || !editMode) return;
        setEditedDetails((prev) => prev?.filter((_, idx) => idx !== index) || []);
    };

    const handleSave = async () => {
        if (!order) return;
        try {
            if (editMode && canEditCommercials && editedCustomerId === '') {
                toast.error('Customer is required for direct sale order');
                return;
            }
            if (editedVatPercent < 0 || editedVatPercent > 100) {
                toast.error('VAT must be between 0 and 100');
                return;
            }
            if (editedDiscountPercent < 0 || editedDiscountPercent > 100) {
                toast.error('Discount must be between 0 and 100');
                return;
            }
            setSaving(true);
            const detailsPayload = canEditLineItems ? (editedDetails || []).map((item) => ({
                so_detail_id: item?.so_detail_id !== undefined ? Number(item.so_detail_id) : undefined,
                estimate_part_id: item?.estimate_part_id !== undefined ? Number(item.estimate_part_id) : undefined,
                part_name: item?.part_name || '',
                quantity: Number(item?.quantity || 0),
                unit_price: Number(item?.unit_price || 0),
                category: item?.category,
                color_finish: item?.color_finish,
                item_id: item?.item_id ? Number(item.item_id) : undefined
            })) : [];
            const buildLineKey = (line: { so_detail_id?: number; estimate_part_id?: number }) =>
                line.so_detail_id !== undefined && line.so_detail_id !== null
                    ? `sd:${Number(line.so_detail_id)}`
                    : `ep:${Number(line.estimate_part_id || 0)}`;
            const originalByKey = new Map<string, NonNullable<SaleOrder['details']>[number]>();
            (order.details || []).forEach((line) => {
                originalByKey.set(buildLineKey(line), line);
            });
            const unitPriceChanged = (detailsPayload || []).some((line) => {
                const original = originalByKey.get(buildLineKey(line));
                if (!original) return false;
                return Math.abs(Number(original.unit_price || 0) - Number(line.unit_price || 0)) > 0.001;
            });
            if (!isDirectOrder && unitPriceChanged && !editedOverrideReason.trim()) {
                toast.error('Override reason is required when unit price is changed');
                return;
            }
            if (canEditLineItems && detailsPayload.length === 0) {
                toast.error('At least one line item is required');
                return;
            }
            if ((detailsPayload || []).some((line) => Number(line.quantity || 0) <= 0)) {
                toast.error('Line quantity must be greater than zero');
                return;
            }

            await legacyApi.sales.updateOrder(order.so_id, {
                customer_id: editMode && canEditCommercials && editedCustomerId !== '' ? Number(editedCustomerId) : undefined,
                customer_po_no: editMode && canEditCommercials ? editedCustomerPoNo : undefined,
                so_date: editMode && canEditCommercials ? (editedSoDate || undefined) : undefined,
                po_issue_date: editMode && canEditCommercials ? (editedPoIssueDate || undefined) : undefined,
                flow_mode: editMode && canEditFlowMode ? editedFlowMode : undefined,
                so_remarks: editedRemarks,
                po_remarks: editedPoRemarks,
                vat_percent: Number(editedVatPercent || 0),
                discount_percent: Number(editedDiscountPercent || 0),
                override_reason: editedOverrideReason.trim() || undefined,
                details: canEditLineItems ? detailsPayload : undefined
            });
            await refreshOrderContext(order.so_id);
            setEditMode(false);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to update sale order');
        } finally {
            setSaving(false);
        }
    };

    const handleTransition = async (action: string) => {
        if (!order) return;
        setTransitionBusy(action);
        try {
            await legacyApi.sales.transitionOrder(order.so_id, action, 'Updated from Sale Order detail page');
            toast.success('Sale order status updated');
            await refreshOrderContext(order.so_id);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to change sale order status');
        } finally {
            setTransitionBusy(null);
        }
    };

    const handleAdminReopen = async () => {
        if (!order) return;
        const confirmed = await confirm(`Reopen ${formatSoId(order.so_id)} to Draft?`, {
            description: 'This will reopen dispatched delivery notes to Draft and reverse stock/COGS for those deliveries. Active invoices must be cancelled first.',
            confirmText: 'Reopen',
        });
        if (!confirmed) return;
        setTransitionBusy('admin_reopen');
        try {
            const result = await legacyApi.sales.adminReopenOrder(order.so_id, 'Admin reopen for correction');
            toast.success(result?.message || 'Sale order reopened to Draft');
            await refreshOrderContext(order.so_id);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to reopen sale order');
        } finally {
            setTransitionBusy(null);
        }
    };

    const handleCreateWorkOrder = async () => {
        if (!order || !canCreateWorkOrder) return;
        const ok = await confirm('Create work order from this sale order?', {
            description: isDirectOrder
                ? 'The work order will be created in Draft and lines will be copied from remaining SO quantities.'
                : 'The work order will be created in Draft and linked to this sale order.',
            confirmText: 'Create Work Order',
        });
        if (!ok) return;
        try {
            setCreatingWorkOrder(true);
            const payload: any = {
                remarks: `Production for SO-${order.so_id}`,
                // Always create WO from SO context so planned lines come from SO details.
                so_id: Number(order.so_id),
            };
            const wo = await api.manufacturing.createWorkOrder(payload);
            const woNumber = wo?.wo_number || (wo?.wo_id ? `WO-${wo.wo_id}` : 'Work Order');
            toast.success(`${woNumber} created in Draft`);
            await refreshOrderContext(order.so_id);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to create work order');
        } finally {
            setCreatingWorkOrder(false);
        }
    };

    const loadConsolidatedInvoicePreview = async (cutoffDate = consolidatedCutoffDate) => {
        if (!order) return;
        try {
            setLoadingConsolidatedPreview(true);
            const preview = await legacyApi.sales.previewUnbilledDeliveryInvoice({
                so_id: Number(order.so_id),
                mode: 'cutoff',
                cutoff_date: cutoffDate,
            });
            setConsolidatedPreview(preview);
        } catch (e: any) {
            setConsolidatedPreview(null);
            toast.error(e?.message || 'Failed to preview unbilled deliveries');
        } finally {
            setLoadingConsolidatedPreview(false);
        }
    };

    const openConsolidatedInvoicePanel = () => {
        setShowConsolidatedInvoicePanel(true);
        setConsolidatedPreview(null);
        window.setTimeout(() => {
            consolidatedInvoicePanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }, 0);
        loadConsolidatedInvoicePreview();
    };

    const handleCreateConsolidatedInvoice = async () => {
        if (!order) return;
        if (!consolidatedPreview?.can_create) {
            toast.error(consolidatedPreview?.reason || 'No unbilled quantity available.');
            return;
        }
        try {
            setCreatingConsolidatedInvoice(true);
            const created = await legacyApi.sales.createInvoiceFromUnbilledDeliveries({
                so_id: Number(order.so_id),
                mode: 'cutoff',
                cutoff_date: consolidatedCutoffDate,
                invoice_notes: `Consolidated invoice up to ${consolidatedCutoffDate}`,
            });
            toast.success('Consolidated invoice created for delivered unbilled quantities.');
            navigate(`/legacy/invoices/${created.invoice_id}`);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to create consolidated invoice');
        } finally {
            setCreatingConsolidatedInvoice(false);
        }
    };

    const handleUploadDocument = async (payload: { file: File; note?: string; markCurrent: boolean }) => {
        if (!order) return;
        try {
            await legacyApi.sales.uploadOrderDocument(order.so_id, {
                file: payload.file,
                category: 'LPO',
                note: payload.note,
                mark_current: payload.markCurrent,
            });
            toast.success('LPO document uploaded');
            await loadOrderDocuments(order.so_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload document');
            throw error;
        }
    };

    const handleSetCurrentDocument = async (docId: number) => {
        if (!order) return;
        try {
            await legacyApi.sales.setOrderDocumentCurrent(order.so_id, docId);
            toast.success('Current document updated');
            await loadOrderDocuments(order.so_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update current document');
            throw error;
        }
    };

    const handleDownloadDocument = async (docId: number) => {
        if (!order) return;
        try {
            await legacyApi.sales.downloadOrderDocument(order.so_id, docId);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to download document');
            throw error;
        }
    };

    const handlePreviewDocument = (doc: DocumentAttachment) => {
        setPreviewDocument(doc);
    };

    const handleDeleteDocument = async (docId: number) => {
        if (!order) return;
        const allowed = await confirm('Delete this LPO document?', {
            description: 'The document will be archived (soft deleted) and removed from the active list.',
            confirmText: 'Delete',
            variant: 'destructive',
        });
        if (!allowed) return;
        try {
            await legacyApi.sales.deleteOrderDocument(order.so_id, docId);
            toast.success('Document deleted');
            await loadOrderDocuments(order.so_id, { silent: true });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to delete document');
            throw error;
        }
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!order) return;
        const result = await legacyApi.sales.emailSaleOrderPdf(order.so_id, payload);
        toast.success(result.message || 'Sale order PDF emailed successfully');
    };

    const formatSoId = (id: number) => `HC/SO/${String(id).padStart(4, '0')}`;
    const formatQty = (value: number | string | null | undefined) =>
        Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
    const formatMoney = (value: number | string | null | undefined) =>
        Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleShareLink = async () => {
        if (!order) return;
        try {
            const result = await legacyApi.sales.createOrderShareLink(order.so_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Sales order share link created and copied' : 'Sales order share link copied');
            } catch {
                window.prompt('Copy sales order share link', result.share_url);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create sales order share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!order) return;
        try {
            const result = await legacyApi.sales.createOrderShareLink(order.so_id, { channel: 'WHATSAPP' });
            const message = `Please find sales order ${formatSoId(order.so_id)}: ${result.share_url}`;
            const customer = customerOptions.find((entry) => Number(entry.customer_id) === Number(order.customer_id));
            const resolved = openWhatsAppMessage(message, customer);
            if (!resolved.normalizedNumber) {
                toast.warning('No valid customer WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open WhatsApp share');
        }
    };
    const formatInvoiceId = (invoiceId: number) => `HC/INV/${String(invoiceId).padStart(4, '0')}`;
    const saleOrderRecipientEmail =
        customerOptions.find((customer) => Number(customer.customer_id) === Number(order?.customer_id))?.email_address || '';
    const filteredDeliveries = useMemo(() => {
        const query = state.q.trim().toLowerCase();
        if (!query) return deliveries;
        return deliveries.filter((dn) =>
            String(dn.dn_number || '').toLowerCase().includes(query) ||
            String(dn.dn_id || '').includes(query) ||
            String(dn.item_count || '').includes(query) ||
            String(getDeliveryStatusMeta(Number(dn.status_id || 1)).label || '').toLowerCase().includes(query)
        );
    }, [deliveries, state.q]);

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (!order) return <div className="p-8 text-center text-red-500">Order not found</div>;
    const displayStatusMeta = getSaleOrderStatusMeta(order.so_status_id || 1, order.status_name);
    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === (order.so_status_id || 1));
    const isDraft = Number(order.so_status_id || 1) === 1;
    const statusId = Number(order.so_status_id || 1);
    const dispatchProgressId = Number(order.dispatch_progress_id || 1);
    const invoiceProgressId = Number(order.invoice_progress_id || 1);
    const dispatchProgressName = order.dispatch_progress_name || (dispatchProgressId === 3 ? 'Fully Dispatched' : dispatchProgressId === 2 ? 'Partially Dispatched' : 'Not Dispatched');
    const invoiceProgressName = order.invoice_progress_name || (invoiceProgressId === 3 ? 'Fully Invoiced' : invoiceProgressId === 2 ? 'Partially Invoiced' : 'Not Invoiced');
    const isDirectOrder = Number(order.estimate_id || 0) <= 0;
    const canEditCommercials = isDirectOrder && isDraft;
    const flowGuards = overview?.flow_guards || {};
    const canEditLineItems = Boolean(order.can_edit_lines ?? flowGuards?.can_edit_lines ?? (isDirectOrder && isDraft));
    const canEditUnitPrice = Boolean(order.can_edit_price ?? flowGuards?.can_edit_price ?? canEditLineItems);
    const lineEditLockReason = String(order.edit_lock_reason || flowGuards?.edit_lock_reason || '');
    const showLineEditLock = !canEditLineItems && lineEditLockReason.trim().length > 0;
    const canCreateWorkOrder = hasPermission('production.wo.create') && !editMode && Boolean(flowGuards?.can_create_work_order ?? (statusId >= 2 && statusId !== 7));
    const createWorkOrderReason = String(flowGuards?.create_work_order_reason || '');
    const canCreateDelivery = hasPermission('sales.delivery.create') && !editMode && Boolean(flowGuards?.can_create_delivery ?? (statusId === 4));
    const createDeliveryReason = String(flowGuards?.create_delivery_reason || '');
    const canCreateProforma = hasPermission('finance.invoice.create') && !editMode && Boolean(flowGuards?.can_create_proforma_invoice ?? (statusId >= 2 && statusId !== 7));
    const createProformaReason = String(flowGuards?.create_proforma_reason || '');
    const canAdminReopen = hasPermission('sales.so.reopen') && !editMode && statusId !== 1 && statusId !== 7;
    const canViewSoDocuments = hasPermission('sales.so.document.view');
    const canUploadSoDocuments = hasPermission('sales.so.document.upload');
    const canDownloadSoDocuments = hasPermission('sales.so.document.download');
    const canDeleteSoDocuments = hasPermission('sales.so.document.delete');
    const canViewInvoices = hasPermission('finance.invoice.view');
    const selectiveFlowEnabled = Boolean(flowGuards?.selective_mode_enabled);
    const flowModeLocked = Boolean(flowGuards?.flow_mode_locked ?? order.flow_mode_locked);
    const canEditFlowMode = selectiveFlowEnabled && isDraft;
    const resolvedFlowMode = String(flowGuards?.resolved_flow_mode || order.resolved_flow_mode || '').trim().toUpperCase();
    const explicitFlowMode = String(order.flow_mode || '').trim().toUpperCase();
    const effectiveFlowMode = resolvedFlowMode || explicitFlowMode || 'PRODUCTION';
    const simpleModeEnabled = effectiveFlowMode === 'SIMPLE' || Boolean(flowGuards?.simple_mode_enabled);
    const overviewWarnings: string[] = Array.isArray(overview?.warnings)
        ? overview.warnings.filter((w: unknown): w is string => typeof w === 'string' && w.trim().length > 0)
        : [];
    const relatedOpenOrders = order.related_open_orders || overview?.related_open_orders || [];
    const topLifecycleActionOrder = ['confirm', 'cancel'];
    const topLifecycleActions = topLifecycleActionOrder
        .map((eventCode) => nextActions.find((action) => action.event_code === eventCode))
        .filter((action): action is WorkflowEventDefinition => Boolean(action));
    const transitionHiddenEvents = simpleModeEnabled
        ? ['dispatch', 'start_production', 'ready_to_ship', ...topLifecycleActionOrder]
        : ['dispatch', ...topLifecycleActionOrder];
    const currentDetails = (editMode ? editedDetails : order.details) || [];
    const showActionColumn = editMode && canEditLineItems;
    const orderItemsFooterLabelColSpan = 11;
    const progressBadgeClass = (id: number) =>
        id === 3
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
            : id === 2
                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                : 'bg-slate-100 text-slate-600 border border-slate-200';
    const resolveWorkOrderStatusLabel = (wo: any) => {
        const raw = String(wo.status || '').trim();
        const normalized = raw.replace(/\s+/g, ' ').toLowerCase();
        const normalizedMap: Record<string, string> = {
            'draft': 'Draft',
            'planned': 'Draft',
            'inprogress': 'In Progress',
            'in progress': 'In Progress',
            'in-production': 'In Progress',
            'in production': 'In Progress',
            'completed': 'Completed',
            'complete': 'Completed',
            'cancelled': 'Cancelled',
            'canceled': 'Cancelled',
        };
        if (normalized && normalizedMap[normalized]) return normalizedMap[normalized];
        if (raw) return raw;
        const statusId = Number(wo.status_id || 1);
        if (statusId === 3) return 'Completed';
        if (statusId === 2) return 'In Progress';
        if (statusId === 4 || statusId === 7) return 'Cancelled';
        return 'Draft';
    };
    const resolveWorkOrderStatusClass = (label: string, statusId: number) => {
        if (label === 'Completed' || statusId === 3) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (label === 'In Progress' || statusId === 2) return 'bg-amber-100 text-amber-800 border-amber-200';
        if (label === 'Cancelled' || statusId === 4 || statusId === 7) return 'bg-red-100 text-red-700 border-red-200';
        return 'bg-slate-100 text-slate-600 border border-slate-200';
    };
    const overviewInvoices = overview?.invoices || [];
    const invoiceColumns = [
        {
            header: "Invoice Number",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) => (
                <span className="font-medium">{formatInvoiceId(Number(invoice.invoice_id || 0))}</span>
            ),
            className: 'font-medium'
        },
        {
            header: "Date",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) =>
                invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '-',
        },
        {
            header: "Type",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) =>
                String(invoice.invoice_type || 'TAX').toUpperCase(),
        },
        {
            header: "Amount",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) =>
                Number(invoice.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            className: 'text-right'
        },
        {
            header: "Status",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) => {
                const status = getInvoiceStatusMeta(Number(invoice.status_id || 1), invoice.status_name);
                return (
                    <span className={`px-2 py-1 rounded-full text-xs border ${status.badgeClass}`}>
                        {status.label}
                    </span>
                );
            },
        },
        {
            header: "Action",
            accessor: (invoice: NonNullable<SaleOrderOverview['invoices']>[number]) => (
                <div className="flex justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/legacy/invoices/${Number(invoice.invoice_id || 0)}`)}
                    >
                        <Eye className="h-4 w-4 mr-2" /> View
                    </Button>
                </div>
            ),
            className: "text-right"
        }
    ];

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
            <DocumentPreviewDialog
                open={Boolean(previewDocument)}
                document={previewDocument}
                previewUrl={previewDocument && order ? legacyApi.sales.previewOrderDocumentUrl(order.so_id, previewDocument.doc_id) : null}
                onOpenChange={(open) => !open && setPreviewDocument(null)}
                onDownload={(doc) => handleDownloadDocument(doc.doc_id)}
            />
            <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0 flex flex-col gap-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
                <div className="flex min-w-0 items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/orders')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold leading-tight break-words">{`Sale Order ${formatSoId(order.so_id)}`}</h1>
                </div>
                {/* ... (Keep existing header buttons if needed, or refine them) ... */}
                <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:justify-end">
                    {!editMode && topLifecycleActions.map((action) => (
                        <Button
                            key={`so-top-action-${action.event_code}`}
                            variant={action.event_code === 'cancel' ? 'destructive' : 'default'}
                            className="shrink-0 whitespace-nowrap"
                            onClick={() => handleTransition(action.event_code)}
                            disabled={transitionBusy !== null}
                        >
                            {transitionBusy === action.event_code ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : null}
                            {action.event_label}
                        </Button>
                    ))}
                    {canAdminReopen && (
                        <Button
                            variant="outline"
                            className="shrink-0 whitespace-nowrap"
                            onClick={handleAdminReopen}
                            disabled={transitionBusy !== null}
                            title="Admin-only rollback to Draft"
                        >
                            {transitionBusy === 'admin_reopen' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Reopen to Draft
                        </Button>
                    )}
                    {!editMode ? (
                        <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={() => setEditMode(true)}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                        </Button>
                    ) : (
                        <>
                            <Button variant="default" className="shrink-0 whitespace-nowrap" onClick={handleSave} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save
                            </Button>
                            <Button
                                variant="ghost"
                                className="shrink-0 whitespace-nowrap"
                                onClick={() => {
                                    setEditedDetails(order.details ? [...order.details] : []);
                                    setEditedRemarks(order.so_remarks || '');
                                    setEditedPoRemarks(order.po_remarks || '');
                                    setEditedVatPercent(Number(order.vat_percent || 0));
                                    setEditedDiscountPercent(Number(order.discount_percent || 0));
                                    setEditedOverrideReason('');
                                    setEditedCustomerId(typeof order.customer_id === 'number' ? Number(order.customer_id) : '');
                                    setEditedCustomerPoNo(order.customer_po_no || '');
                                    setEditedSoDate(toDateInputValue(order.so_date));
                                    setEditedPoIssueDate(toDateInputValue(order.po_issue_date));
                                    setEditedFlowMode(
                                        String(order.flow_mode || order.resolved_flow_mode || 'PRODUCTION').trim().toUpperCase() === 'SIMPLE'
                                            ? 'SIMPLE'
                                            : 'PRODUCTION'
                                    );
                                    setEditMode(false);
                                }}
                                disabled={saving}
                            >
                                <X className="h-4 w-4 mr-2" /> Cancel
                            </Button>
                        </>
                    )}
                    <Button className="shrink-0 whitespace-nowrap" variant="outline" onClick={async () => {
                        try {
                            if (order) await legacyApi.sales.downloadPdf(order.so_id);
                        } catch (e) {
                            toast.error('Failed to download PDF');
                            console.error(e);
                        }
                    }}>
                        <Download className="h-4 w-4 mr-2" /> Sale Order
                    </Button>
                    {hasPermission('sales.so.email') && (
                        <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={() => setShowSendPdfModal(true)}>
                            <Send className="h-4 w-4 mr-2" /> Send
                        </Button>
                    )}
                    {hasPermission('sales.so.share') && (
                        <>
                            <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={handleShareLink}>
                                <Link2 className="h-4 w-4 mr-2" /> Share Link
                            </Button>
                            <Button variant="outline" className="shrink-0 whitespace-nowrap" onClick={handleShareWhatsApp}>
                                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                            </Button>
                        </>
                    )}
                    <Button
                        variant="destructive"
                        className="shrink-0 whitespace-nowrap"
                        onClick={async () => {
                            if (!order) return;
                            const confirmed = await confirm(`Delete ${formatSoId(order.so_id)}?`, {
                                description: 'This action cannot be undone.',
                                confirmText: 'Delete',
                                variant: 'destructive'
                            });
                            if (!confirmed) return;
                            try {
                                await legacyApi.sales.deleteOrder(order.so_id);
                                toast.success('Sale order deleted');
                                navigate('/legacy/orders');
                            } catch (e: any) {
                                toast.error(e?.message || 'Failed to delete sale order');
                                console.error(e);
                            }
                        }}
                    >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                    <Button
                        variant="default"
                        className="shrink-0 whitespace-nowrap"
                        disabled={!canCreateDelivery}
                        title={!canCreateDelivery ? createDeliveryReason : 'Create delivery note'}
                        onClick={() => navigate(`/legacy/delivery/create/${order.so_id}`)}
                    >
                        <Truck className="h-4 w-4 mr-2" /> Create Delivery
                    </Button>
                    {hasPermission('production.wo.create') && (
                        <Button
                            variant="outline"
                            className="shrink-0 whitespace-nowrap"
                            onClick={handleCreateWorkOrder}
                            disabled={creatingWorkOrder || !canCreateWorkOrder}
                            title={!canCreateWorkOrder ? createWorkOrderReason : 'Create work order'}
                        >
                            {creatingWorkOrder ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Create Work Order
                        </Button>
                    )}
                    {hasPermission('finance.invoice.create') && (
                        <>
                            <Button
                                variant="outline"
                                className="shrink-0 whitespace-nowrap text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                disabled={!canCreateProforma}
                                title={!canCreateProforma ? createProformaReason : 'Create proforma invoice'}
                                onClick={() => navigate(`/legacy/invoices/create/${order.so_id}?invoice_type=PROFORMA`)}
                            >
                                <CreditCard className="h-4 w-4 mr-2" /> Create Proforma
                            </Button>
                            <Button
                                variant="outline"
                                className="shrink-0 whitespace-nowrap text-blue-700 border-blue-200 hover:bg-blue-50"
                                disabled={editMode}
                                title="Preview delivered and unbilled DNs up to a cut-off date"
                                onClick={openConsolidatedInvoicePanel}
                            >
                                <CreditCard className="h-4 w-4 mr-2" /> Create Consolidated Invoice
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-6">
                <SendPdfModal
                    open={showSendPdfModal}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Sales Order"
                    documentNumber={formatSoId(order.so_id)}
                    attachmentName={`${formatSoId(order.so_id).replace(/\//g, '_')}.pdf`}
                    defaultTo={saleOrderRecipientEmail}
                    defaultSubject={`Sales Order ${formatSoId(order.so_id)}`}
                    defaultBody={`Dear Customer,\n\nPlease find attached sales order ${formatSoId(order.so_id)}.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPdf}
                />
                {overviewWarnings.length > 0 && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <ul className="list-disc pl-5 space-y-1">
                            {overviewWarnings.map((warning, index) => (
                                <li key={`so-overview-warning-${index}`}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {showConsolidatedInvoicePanel && (
                    <Card ref={consolidatedInvoicePanelRef} className="border-blue-200 bg-blue-50/40">
                        <CardHeader className="flex flex-row items-start justify-between gap-4">
                            <div>
                                <CardTitle>Create Consolidated Invoice</CardTitle>
                                <p className="mt-1 text-sm text-slate-600">
                                    Existing posted invoices will not be changed. This invoice will include only delivered and unbilled quantities.
                                </p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setShowConsolidatedInvoicePanel(false)}>
                                <X className="h-4 w-4" />
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <label htmlFor="consolidated-invoice-cutoff" className="text-xs text-muted-foreground">Cut-off date</label>
                                    <input
                                        id="consolidated-invoice-cutoff"
                                        type="date"
                                        className="mt-1 rounded-md border px-3 py-2 text-sm"
                                        value={consolidatedCutoffDate}
                                        onChange={(event) => {
                                            const nextDate = event.target.value;
                                            setConsolidatedCutoffDate(nextDate);
                                            setConsolidatedPreview(null);
                                        }}
                                    />
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={() => loadConsolidatedInvoicePreview(consolidatedCutoffDate)}
                                    disabled={loadingConsolidatedPreview || !consolidatedCutoffDate}
                                >
                                    {loadingConsolidatedPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                    Preview
                                </Button>
                                <Button
                                    onClick={handleCreateConsolidatedInvoice}
                                    disabled={creatingConsolidatedInvoice || loadingConsolidatedPreview || !consolidatedPreview?.can_create}
                                    title={!consolidatedPreview?.can_create ? (consolidatedPreview?.reason || 'No unbilled quantity available') : 'Create invoice'}
                                >
                                    {creatingConsolidatedInvoice ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                                    Create Invoice
                                </Button>
                            </div>

                            {consolidatedPreview && !consolidatedPreview.can_create && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                    {consolidatedPreview.reason || 'No unbilled quantity available.'}
                                </div>
                            )}

                            {consolidatedPreview && (
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                                    <div className="lg:col-span-2 overflow-hidden rounded-md border bg-white">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-600">Included DN</th>
                                                    <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                                                    <th className="px-3 py-2 text-right font-medium text-slate-600">Invoiceable Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                                {consolidatedPreview.included_dns.length > 0 ? (
                                                    consolidatedPreview.included_dns.map((dn) => (
                                                        <tr key={`included-dn-${dn.dn_id}`}>
                                                            <td className="px-3 py-2 font-medium">{dn.dn_number}</td>
                                                            <td className="px-3 py-2 text-slate-600">
                                                                {dn.dn_date ? new Date(dn.dn_date).toLocaleDateString() : '-'}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-medium">{formatQty(dn.invoiceable_qty)}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td className="px-3 py-3 text-sm text-slate-500" colSpan={3}>
                                                            No unbilled quantity available.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="rounded-md border bg-white p-3 text-sm">
                                        <div className="flex justify-between border-b py-1">
                                            <span className="text-slate-600">Subtotal</span>
                                            <span className="font-medium">{formatMoney(consolidatedPreview.subtotal)}</span>
                                        </div>
                                        <div className="flex justify-between border-b py-1">
                                            <span className="text-slate-600">VAT ({Number(consolidatedPreview.vat_percent || 0).toFixed(2)}%)</span>
                                            <span className="font-medium">{formatMoney(consolidatedPreview.vat_amount)}</span>
                                        </div>
                                        <div className="flex justify-between pt-2 text-base font-semibold">
                                            <span>Grand Total</span>
                                            <span>{formatMoney(consolidatedPreview.grand_total)}</span>
                                        </div>
                                        {consolidatedPreview.excluded_dns.length > 0 && (
                                            <div className="mt-3 text-xs text-slate-500">
                                                {consolidatedPreview.excluded_dns.length} DN(s) excluded by status, cut-off date, or already invoiced quantity.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
                <RelatedOpenSaleOrdersNotice orders={relatedOpenOrders} customerName={order.customer_name} />
                <div className="space-y-6">
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader><CardTitle>Customer Details</CardTitle></CardHeader>
                                <CardContent className="space-y-2">
                                    {editMode && canEditCommercials ? (
                                        <>
                                            <div>
                                                <label htmlFor="so-customer-id" className="text-xs text-muted-foreground">Customer</label>
                                                <select
                                                    id="so-customer-id"
                                                    className="w-full mt-1 rounded border px-3 py-2 text-sm bg-white"
                                                    value={editedCustomerId === '' ? '' : String(editedCustomerId)}
                                                    onChange={(e) => {
                                                        const next = e.target.value;
                                                        setEditedCustomerId(next ? Number(next) : '');
                                                    }}
                                                >
                                                    <option value="">Select customer</option>
                                                    {customerOptions.map((customer) => (
                                                        <option key={customer.customer_id} value={customer.customer_id}>
                                                            {customer.customer_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label htmlFor="so-customer-po-no" className="text-xs text-muted-foreground">PO Number</label>
                                                <input
                                                    id="so-customer-po-no"
                                                    className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                    value={editedCustomerPoNo}
                                                    onChange={(e) => setEditedCustomerPoNo(e.target.value)}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label htmlFor="so-date" className="text-xs text-muted-foreground">Date</label>
                                                    <input
                                                        id="so-date"
                                                        type="date"
                                                        className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                        value={editedSoDate}
                                                        onChange={(e) => setEditedSoDate(e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="so-po-issue-date" className="text-xs text-muted-foreground">PO Issue Date</label>
                                                    <input
                                                        id="so-po-issue-date"
                                                        type="date"
                                                        className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                        value={editedPoIssueDate}
                                                        onChange={(e) => setEditedPoIssueDate(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="text-muted-foreground">Customer</span>
                                                <span className="font-medium">{order.customer_name}</span>
                                            </div>
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="text-muted-foreground">PO Number</span>
                                                <span className="font-medium">{order.customer_po_no || 'N/A'}</span>
                                            </div>
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="text-muted-foreground">Date</span>
                                                <span className="font-medium">{order.so_date ? new Date(order.so_date).toLocaleDateString() : '-'}</span>
                                            </div>
                                            <div className="flex justify-between border-b pb-2">
                                                <span className="text-muted-foreground">PO Issue Date</span>
                                                <span className="font-medium">{order.po_issue_date ? new Date(order.po_issue_date).toLocaleDateString() : '-'}</span>
                                            </div>
                                        </>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader><CardTitle>Commercial Notes</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="mb-4 rounded-md border bg-slate-50 px-3 py-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-xs text-muted-foreground">Flow Mode</div>
                                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs border ${simpleModeEnabled ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                                        {simpleModeEnabled ? 'Simple' : 'Production'}
                                                    </span>
                                                    <span className="text-xs text-slate-500">
                                                        {selectiveFlowEnabled
                                                            ? flowModeLocked && !isDraft
                                                                ? 'Locked after confirmation or downstream activity.'
                                                                : flowModeLocked
                                                                    ? 'Draft mode can be changed if no active downstream activity remains.'
                                                                    : 'Saved at sale-order level.'
                                                            : 'Using the current global default/fallback setting.'}
                                                    </span>
                                                </div>
                                            </div>
                                            {editMode && canEditFlowMode ? (
                                                <div className="min-w-[220px]">
                                                    <label htmlFor="so-flow-mode" className="text-xs text-muted-foreground">Choose Flow</label>
                                                    <select
                                                        id="so-flow-mode"
                                                        className="w-full mt-1 rounded border px-3 py-2 text-sm bg-white"
                                                        value={editedFlowMode}
                                                        onChange={(e) => setEditedFlowMode(e.target.value === 'SIMPLE' ? 'SIMPLE' : 'PRODUCTION')}
                                                    >
                                                        <option value="PRODUCTION">Production</option>
                                                        <option value="SIMPLE">Simple</option>
                                                    </select>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs text-muted-foreground">Remarks</label>
                                        {editMode ? (
                                            <textarea
                                                className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                rows={3}
                                                value={editedRemarks}
                                                onChange={(e) => setEditedRemarks(e.target.value)}
                                            />
                                        ) : (
                                            <div className="mt-2 p-3 bg-yellow-50 text-yellow-800 text-sm rounded">
                                                {order.so_remarks || 'Subject to availability of material.'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-4">
                                        <label className="text-xs text-muted-foreground">Internal Remarks (PO)</label>
                                        {editMode ? (
                                            <textarea
                                                className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                rows={3}
                                                value={editedPoRemarks}
                                                onChange={(e) => setEditedPoRemarks(e.target.value)}
                                            />
                                        ) : (
                                            <div className="mt-2 p-3 bg-slate-50 text-slate-800 text-sm rounded">
                                                {order.po_remarks || '-'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs text-muted-foreground">VAT (%)</label>
                                            {editMode && canEditCommercials ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                    value={editedVatPercent}
                                                    onChange={(e) => setEditedVatPercent(Number(e.target.value || 0))}
                                                />
                                            ) : (
                                                <div className="mt-2 p-3 bg-slate-50 text-slate-800 text-sm rounded">{Number(order.vat_percent || 0).toFixed(2)}%</div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-xs text-muted-foreground">Discount (%)</label>
                                            {editMode && canEditCommercials ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                                    value={editedDiscountPercent}
                                                    onChange={(e) => setEditedDiscountPercent(Number(e.target.value || 0))}
                                                />
                                            ) : (
                                                <div className="mt-2 p-3 bg-slate-50 text-slate-800 text-sm rounded">{Number(order.discount_percent || 0).toFixed(2)}%</div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        {canViewSoDocuments && (
                            <DocumentAttachmentsCard
                                title="LPO Documents"
                                emptyLabel="No LPO documents uploaded."
                                documents={documents}
                                loading={documentsLoading}
                                canUpload={canUploadSoDocuments}
                                canDownload={canDownloadSoDocuments}
                                canDelete={canDeleteSoDocuments}
                                onUpload={handleUploadDocument}
                                onSetCurrent={handleSetCurrentDocument}
                                onPreview={handlePreviewDocument}
                                onDownload={handleDownloadDocument}
                                onDelete={handleDeleteDocument}
                                onRefresh={() => loadOrderDocuments(order.so_id)}
                            />
                        )}
                    </div>
                    <WorkflowRightSidebar mode="overlay" title="SO Workflow & Status">
                        <WorkflowRightPanel
                            title="Workflow & Status"
                            description="Next actions, status, and prechecks"
                        >
                            <div className="flex items-center gap-2 p-2 bg-slate-100 rounded">
                                <FileText className="h-4 w-4 text-slate-500" />
                                <span className="text-slate-700">Estimate Reference:</span>
                                {order.estimate_id && order.estimate_id > 0 ? (
                                    <a href={`/legacy/estimates/${order.estimate_id}`} className="text-blue-600 hover:underline font-medium">
                                        EST-{order.estimate_id}
                                    </a>
                                ) : (
                                    <span className="font-medium text-slate-700">Direct Sale Order</span>
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Status:{' '}
                                <span className={`px-2 py-1 rounded-full text-xs ${displayStatusMeta.badgeClass}`}>
                                    {displayStatusMeta.label}
                                </span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Dispatch Progress:{' '}
                                <span className={`px-2 py-1 rounded-full text-xs ${progressBadgeClass(dispatchProgressId)}`}>
                                    {dispatchProgressName}
                                </span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Invoice Progress:{' '}
                                <span className={`px-2 py-1 rounded-full text-xs ${progressBadgeClass(invoiceProgressId)}`}>
                                    {invoiceProgressName}
                                </span>
                            </div>
                            {isDirectOrder && (
                                <div className="text-xs text-slate-500 rounded border bg-slate-50 px-2 py-1">
                                    Direct sale order: keep it in <span className="font-semibold">Draft</span> while editing lines and commercial terms, then use <span className="font-semibold">Confirm</span> to start lifecycle.
                                </div>
                            )}
                            {statusId >= 2 && statusId !== 7 && !simpleModeEnabled && (
                                <div className="text-xs text-slate-600 rounded border bg-slate-50 px-2 py-1">
                                    Work orders are created in <span className="font-semibold">Draft</span>. Use <span className="font-semibold">Start</span> in Production when execution begins.
                                </div>
                            )}
                            {simpleModeEnabled && (
                                <div className="text-xs text-blue-700 rounded border border-blue-200 bg-blue-50 px-2 py-1">
                                    Simple flow is active for this sale order: production actions are bypassed and delivery can follow stock availability.
                                </div>
                            )}
                            {showLineEditLock && (
                                <div className="text-xs text-amber-700 rounded border border-amber-300 bg-amber-50 px-2 py-1">
                                    {lineEditLockReason}
                                </div>
                            )}
                            <TransitionActionBar
                                actions={nextActions}
                                onAction={handleTransition}
                                disabled={editMode || transitionBusy !== null}
                                busyEventCode={transitionBusy}
                                precheckEntity="SO"
                                precheckEntityId={order.so_id}
                                hiddenEventCodes={transitionHiddenEvents}
                                actionOrder={['confirm', 'start_production', 'ready_to_ship', 'close', 'cancel']}
                            />
                            <div className="text-xs text-slate-600 rounded border bg-slate-50 px-2 py-1">
                                Dispatch is performed at Delivery Note level; SO dispatch progress updates automatically.
                            </div>
                            <StatusGuidePanel
                                statusLabel={statusDefinition?.status_label || displayStatusMeta.label}
                                statusMeaning={statusDefinition?.status_meaning || ''}
                                nextActions={nextActions}
                            />
                            <FlowCoachPanel entity="SO" entityId={order.so_id} />
                            {!editMode && order.auto_status_id && (
                                <div className="text-xs mt-2">
                                    {order.status_is_manual_override ? (
                                        <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                                            Manual status override. Auto: <span className="font-semibold">{order.auto_status_name || `#${order.auto_status_id}`}</span>
                                        </div>
                                    ) : (
                                        <div className="text-slate-500">
                                            Auto status: <span className="font-medium text-slate-700">{order.auto_status_name || `#${order.auto_status_id}`}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </WorkflowRightPanel>

                        <WorkflowRightPanel
                            title="Status History"
                            description="Audit log of status changes"
                            defaultOpen={false}
                        >
                            <StatusHistoryPanel history={history} />
                        </WorkflowRightPanel>
                    </WorkflowRightSidebar>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Order Items</CardTitle>
                        {editMode && canEditLineItems && (
                            <Button variant="outline" size="sm" onClick={handleAddLine}>
                                <Plus className="h-4 w-4 mr-2" /> Add Line
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {showLineEditLock && (
                            <div className="mb-3 text-sm text-amber-700 rounded border border-amber-300 bg-amber-50 px-3 py-2">
                                {lineEditLockReason}
                            </div>
                        )}
                        {editMode && !isDirectOrder && (
                            <div className="mb-4">
                                <label className="text-xs text-muted-foreground">
                                    Override Reason {canEditUnitPrice ? '(required when unit price changes)' : ''}
                                </label>
                                <input
                                    type="text"
                                    className="w-full mt-1 rounded border px-3 py-2 text-sm"
                                    placeholder="Reason for estimate-linked line override"
                                    value={editedOverrideReason}
                                    onChange={(e) => setEditedOverrideReason(e.target.value)}
                                />
                            </div>
                        )}
                        {currentDetails.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-medium text-slate-600">Part #</th>
                                            <th className="px-4 py-3 text-left font-medium text-slate-600">Part Name</th>
                                            <th className="px-4 py-3 text-left font-medium text-slate-600">Material Type</th>
                                            <th className="px-4 py-3 text-center font-medium text-slate-600">Thickness</th>
                                            <th className="px-4 py-3 text-left font-medium text-slate-600">Category</th>
                                            <th className="px-4 py-3 text-left font-medium text-slate-600">Color Finish</th>
                                            <th className="px-4 py-3 text-center font-medium text-slate-600">Qty</th>
                                            <th className="px-4 py-3 text-center font-medium text-slate-600">Dispatched</th>
                                            <th className="px-4 py-3 text-center font-medium text-slate-600">Pending</th>
                                            <th className="px-4 py-3 text-center font-medium text-slate-600">Unit</th>
                                            <th className="px-4 py-3 text-right font-medium text-slate-600">Unit Price</th>
                                            <th className="px-4 py-3 text-right font-medium text-slate-600">Amount</th>
                                            {showActionColumn && (
                                                <th className="px-4 py-3 text-center font-medium text-slate-600">Action</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {currentDetails.map((item, idx) => {
                                            const orderedQty = Number(item.quantity || 0);
                                            const dispatchedQty = Number(item.delivered_qty || 0);
                                            const pendingQty = item.remaining_qty !== undefined
                                                ? Number(item.remaining_qty || 0)
                                                : Math.max(0, orderedQty - dispatchedQty);
                                            const lineAmount = orderedQty * (item.unit_price || 0);
                                            return (
                                                <tr key={idx} className="hover:bg-slate-50/50">
                                                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{item.formatted_part_id || '-'}</td>
                                                    <td className="px-4 py-3">
                                                        {editMode && canEditLineItems ? (
                                                            <input
                                                                type="text"
                                                                className="w-full rounded border px-2 py-1"
                                                                value={item.part_name || ''}
                                                                onChange={(e) => handleDetailFieldChange(idx, 'part_name', e.target.value)}
                                                            />
                                                        ) : (
                                                            item.part_name
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-500">{item.material_type || item.material_name || '-'}</td>
                                                    <td className="px-4 py-3 text-center text-slate-500">{item.thickness ?? '-'}</td>
                                                    <td className="px-4 py-3">
                                                        {editMode && canEditLineItems ? (
                                                            <input
                                                                type="text"
                                                                className="w-full rounded border px-2 py-1"
                                                                value={item.category || ''}
                                                                onChange={(e) => handleDetailFieldChange(idx, 'category', e.target.value)}
                                                            />
                                                        ) : (
                                                            item.category || '-'
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {editMode && canEditLineItems ? (
                                                            <input
                                                                type="text"
                                                                className="w-full rounded border px-2 py-1"
                                                                value={item.color_finish || ''}
                                                                onChange={(e) => handleDetailFieldChange(idx, 'color_finish', e.target.value)}
                                                            />
                                                        ) : (
                                                            item.color_finish || '-'
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {editMode && canEditLineItems ? (
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="1"
                                                                className="w-20 rounded border px-2 py-1 text-center"
                                                                value={item.quantity}
                                                                onChange={(e) => handleDetailFieldChange(idx, 'quantity', Number(e.target.value))}
                                                            />
                                                        ) : (
                                                            item.quantity
                                                        )}
                                                    </td>
                                                    <td
                                                        className="px-4 py-3 text-center font-medium text-emerald-700"
                                                        aria-label={`${item.part_name || item.formatted_part_id || `Line ${idx + 1}`} dispatched ${formatQty(dispatchedQty)} of ${formatQty(orderedQty)}`}
                                                    >
                                                        {formatQty(dispatchedQty)}
                                                    </td>
                                                    <td
                                                        className="px-4 py-3 text-center font-medium text-amber-700"
                                                        aria-label={`${item.part_name || item.formatted_part_id || `Line ${idx + 1}`} pending ${formatQty(pendingQty)}`}
                                                    >
                                                        {formatQty(pendingQty)}
                                                    </td>
                                                    <td className="px-4 py-3 text-center text-slate-500">{item.unit_label || item.unit || '-'}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        {editMode && canEditUnitPrice ? (
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                className="w-28 rounded border px-2 py-1 text-right"
                                                                value={item.unit_price || 0}
                                                                onChange={(e) => handleDetailFieldChange(idx, 'unit_price', Number(e.target.value || 0))}
                                                            />
                                                        ) : (
                                                            item.unit_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium">{lineAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                    {showActionColumn && (
                                                        <td className="px-4 py-3 text-center">
                                                            <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleRemoveLine(idx)}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot className="bg-slate-50 border-t">
                                        {(() => {
                                            const subtotal = currentDetails.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0);
                                            const discountPercent = editMode ? editedDiscountPercent : Number(order.discount_percent || 0);
                                            const vatPercent = editMode ? editedVatPercent : Number(order.vat_percent || 0);

                                            const discountAmount = (discountPercent / 100) * subtotal;
                                            const afterDiscount = subtotal - discountAmount;
                                            const vatAmount = (vatPercent / 100) * afterDiscount;
                                            const grandTotal = afterDiscount + vatAmount;

                                            return (
                                                <>
                                                    <tr className="font-medium">
                                                        <td colSpan={orderItemsFooterLabelColSpan} className="px-4 py-2 text-right text-slate-600">Subtotal</td>
                                                        <td className="px-4 py-2 text-right">
                                                            {subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </td>
                                                        {showActionColumn && <td className="px-4 py-2"></td>}
                                                    </tr>
                                                    {discountPercent > 0 && (
                                                        <tr className="text-sm">
                                                            <td colSpan={orderItemsFooterLabelColSpan} className="px-4 py-1 text-right text-slate-500">
                                                                Discount ({discountPercent}%)
                                                            </td>
                                                            <td className="px-4 py-1 text-right text-red-600">
                                                                -{discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            </td>
                                                            {showActionColumn && <td className="px-4 py-1"></td>}
                                                        </tr>
                                                    )}
                                                    {vatPercent > 0 && (
                                                        <tr className="text-sm">
                                                            <td colSpan={orderItemsFooterLabelColSpan} className="px-4 py-1 text-right text-slate-500">
                                                                VAT ({vatPercent}%)
                                                            </td>
                                                            <td className="px-4 py-1 text-right text-green-600">
                                                                +{vatAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            </td>
                                                            {showActionColumn && <td className="px-4 py-1"></td>}
                                                        </tr>
                                                    )}
                                                    <tr className="font-bold text-lg border-t-2">
                                                        <td colSpan={orderItemsFooterLabelColSpan} className="px-4 py-3 text-right">Grand Total</td>
                                                        <td className="px-4 py-3 text-right text-blue-600">
                                                            {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </td>
                                                        {showActionColumn && <td className="px-4 py-3"></td>}
                                                    </tr>
                                                </>
                                            );
                                        })()}
                                    </tfoot>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-slate-500 italic">
                                No items found for this order. <br />
                                {order.estimate_id && order.estimate_id > 0 ? (
                                    <a href={`/legacy/estimates/${order.estimate_id}`} className="text-blue-500 hover:underline">View Estimate</a>
                                ) : (
                                    <span>This is a direct sale order.</span>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {(order.override_history?.length || 0) > 0 && (
                    <Card>
                        <CardHeader><CardTitle>Line Override History</CardTitle></CardHeader>
                        <CardContent>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-slate-600">When</th>
                                            <th className="px-3 py-2 text-left text-slate-600">Line</th>
                                            <th className="px-3 py-2 text-left text-slate-600">Field</th>
                                            <th className="px-3 py-2 text-right text-slate-600">Old</th>
                                            <th className="px-3 py-2 text-right text-slate-600">New</th>
                                            <th className="px-3 py-2 text-left text-slate-600">Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {(order.override_history || []).map((row) => (
                                            <tr key={String(row.id ?? `${row.so_detail_id}-${row.field_name}-${row.changed_at}`)}>
                                                <td className="px-3 py-2 text-xs text-slate-500">{row.changed_at ? new Date(row.changed_at).toLocaleString() : '-'}</td>
                                                <td className="px-3 py-2">{row.so_detail_id ?? '-'}</td>
                                                <td className="px-3 py-2 capitalize">{row.field_name || '-'}</td>
                                                <td className="px-3 py-2 text-right">{row.old_value ?? '-'}</td>
                                                <td className="px-3 py-2 text-right">{row.new_value ?? '-'}</td>
                                                <td className="px-3 py-2">{row.reason || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {overview && (
                    <Card>
                        <CardHeader><CardTitle>Order Overview</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Ordered Qty</div>
                                    <div className="text-sm font-semibold">{overview?.totals?.ordered_qty ?? 0}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Dispatched Qty</div>
                                    <div className="text-sm font-semibold">{overview?.totals?.dispatched_qty ?? overview?.totals?.delivered_qty ?? 0}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Draft Planned Qty</div>
                                    <div className="text-sm font-semibold">{overview?.totals?.draft_planned_qty ?? 0}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Balance to Dispatch</div>
                                    <div className="text-sm font-semibold">{overview?.totals?.balance_to_dispatch_qty ?? overview?.totals?.remaining_delivery_qty ?? 0}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Invoiced Qty</div>
                                    <div className="text-sm font-semibold">{overview?.totals?.invoiced_qty ?? 0}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] text-muted-foreground">Order Amount</div>
                                    <div className="text-sm font-semibold">
                                        {Number(overview?.totals?.order_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-muted-foreground">Dispatch</span>
                                <span className={`px-2 py-1 rounded-full border ${progressBadgeClass(dispatchProgressId)}`}>
                                    {dispatchProgressName}
                                </span>
                                <span className="text-muted-foreground ml-2">Invoice</span>
                                <span className={`px-2 py-1 rounded-full border ${progressBadgeClass(invoiceProgressId)}`}>
                                    {invoiceProgressName}
                                </span>
                                <span className="ml-2 rounded border px-2 py-1 bg-white text-slate-700">
                                    Dispatched DNs: {overview?.delivery_counts?.dispatched ?? 0}
                                </span>
                                <span className="rounded border px-2 py-1 bg-white text-slate-700">
                                    Draft DNs: {overview?.delivery_counts?.draft ?? 0}
                                </span>
                                <span className="rounded border px-2 py-1 bg-white text-slate-700">
                                    Invoices: {(overview?.invoices || []).length}
                                </span>
                                <span className="rounded border px-2 py-1 bg-white text-slate-700">
                                    Work Orders: {(overview?.work_orders || []).length}
                                </span>
                            </div>

                            <div className="text-xs text-slate-600 rounded border bg-slate-50 px-3 py-2">
                                Draft planned qty includes draft DN quantities. Dispatched qty includes only dispatched DNs.
                            </div>
                            {simpleModeEnabled && (
                                <div className="text-xs text-slate-500">
                                    Production is bypassed for this sale order because it is using Simple flow. Work orders remain informational only.
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Work Orders Section */}
                {overview && (overview.work_orders || []).length > 0 && (
                    <Card>
                        <CardHeader><CardTitle>Work Orders</CardTitle></CardHeader>
                        <CardContent>
                            <CompactTable
                                data={overview.work_orders || []}
                                columns={[
                                    {
                                        header: "WO Number",
                                        accessor: (wo: any) => <span className="font-medium">{wo.wo_number || `WO-${wo.wo_id}`}</span>,
                                        className: 'font-medium'
                                    },
                                    {
                                        header: "Date",
                                        accessor: (wo: any) => wo.wo_date ? new Date(wo.wo_date).toLocaleDateString() : '-'
                                    },
                                    {
                                        header: "Status",
                                        accessor: (wo: any) => {
                                            const status = resolveWorkOrderStatusLabel(wo);
                                            const statusId = Number(wo.status_id || 1);
                                            const colorClass = resolveWorkOrderStatusClass(status, statusId);
                                            return (
                                                <span className={`px-2 py-1 rounded-full text-xs border ${colorClass}`}>
                                                    {status}
                                                </span>
                                            );
                                        }
                                    },
                                    {
                                        header: "Action",
                                        accessor: (wo: any) => (
                                            <Button variant="ghost" size="sm" onClick={() => navigate(`/manufacturing/orders/${wo.wo_id}`)}>
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        ),
                                        className: "text-right"
                                    }
                                ]}
                                keyField="wo_id"
                            />
                        </CardContent>
                    </Card>
                )}


                {/* Deliveries Section */}
                <Card>
                    <CardHeader><CardTitle>Delivery Notes</CardTitle></CardHeader>
                    <CardContent>
                        <TableFilterBar
                            searchValue={state.q}
                            onSearchChange={(value) => setFilter('q', value)}
                            searchPlaceholder="Search delivery notes..."
                            isDirty={isDirty}
                            onClear={clearAll}
                            rightSlot={`${filteredDeliveries.length} results`}
                        />
                        <div className="mt-3">
                            <CompactTable
                                data={filteredDeliveries}
                                columns={deliveryColumns}
                                keyField="dn_id"
                                emptyMessage="No delivery notes linked to this sale order."
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Invoices Section */}
                {canViewInvoices && overview && (
                    <Card>
                        <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
                        <CardContent>
                            <CompactTable
                                data={overviewInvoices}
                                columns={invoiceColumns}
                                keyField="invoice_id"
                                emptyMessage="No invoices linked to this sale order."
                            />
                        </CardContent>
                    </Card>
                )}

            </div>
        </div>
    );
};
